import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getDemoJourneyView,
  applyJourneyView,
  isMainTestAccountEmail,
  JOURNEY_VIEWS,
  DEMO_JOURNEY_EVENT,
  type DemoJourneyView,
} from '../../utils/demoJourney';
import './DemoJourneyToggle.css';

/**
 * Floating dashboard view switcher — VISIBLE ONLY for the hardcoded demo
 * account (test@gmail.com). Lets you flip the dashboard between the new-user
 * (early journey) and in-journey (full) UIs while working, without going to
 * Settings → For Testing or creating a new account. Runs the exact same
 * applyJourneyView() path as the Settings panel, so the two stay in sync via
 * DEMO_JOURNEY_EVENT. Renders nothing (no DOM, no listeners, no calls) for
 * every other account.
 */
const DemoJourneyToggle: React.FC = () => {
  const { user } = useAuth() as any;
  const isTest = isMainTestAccountEmail(user?.email);

  const [view, setView] = useState<DemoJourneyView>(() => (isTest ? getDemoJourneyView() : null));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Stay in sync with the Settings panel (same-tab via the custom event) and
  // other tabs (storage). Only wired up for the test account.
  useEffect(() => {
    if (!isTest) return;
    const sync = () => setView(getDemoJourneyView());
    sync();
    window.addEventListener(DEMO_JOURNEY_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(DEMO_JOURNEY_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [isTest]);

  // Close on outside-click / Escape while the menu is open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!isTest) return null;

  const choose = async (next: DemoJourneyView) => {
    if (busy) return;
    setBusy(true);
    try {
      await applyJourneyView(next); // setView arrives via DEMO_JOURNEY_EVENT
    } catch {
      /* backend triple-gates on the test account; the UI gate already prevents others reaching here */
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const current = JOURNEY_VIEWS.find((v) => v.id === view) || JOURNEY_VIEWS[2];

  return (
    <div className="demo-journey-fab" ref={ref}>
      {open && (
        <div className="demo-journey-fab-menu" role="menu" aria-label="Dashboard journey preview">
          <span className="demo-journey-fab-heading">Preview dashboard as</span>
          {JOURNEY_VIEWS.map((v) => {
            const active = view === v.id;
            return (
              <button
                key={String(v.id)}
                type="button"
                role="menuitemradio"
                disabled={busy}
                aria-checked={active}
                className={`demo-journey-fab-option${active ? ' is-active' : ''}`}
                onClick={() => choose(v.id)}
              >
                <span className="demo-journey-fab-label">{v.label}</span>
                <span className="demo-journey-fab-sub">{v.sub}</span>
              </button>
            );
          })}
        </div>
      )}
      <button
        type="button"
        className="demo-journey-fab-trigger"
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Preview dashboard journey (test account). Currently: ${current.label}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="demo-journey-fab-flask" aria-hidden="true">
          🧪
        </span>
        <span className="demo-journey-fab-trigger-text">{busy ? 'Switching…' : current.label}</span>
      </button>
    </div>
  );
};

export default DemoJourneyToggle;
